import {Directive, effect, ElementRef, inject, input, InputSignal, Renderer2} from "@angular/core";
import {CachedImageService} from "./cached-image.service";
import {FedeltaImageData} from "@fedelta-pos/data-client";
import {CssVarHelper} from '../../utils/css-var.helper';

export class ImageSpec {
    conditional?: boolean;
    on?: boolean;
    image?: FedeltaImageData;
    tiled?: boolean;
    opacity?: number;
    classes?: string;
    cssVars?: Record<string, string | number | boolean>;
}

@Directive({
    selector: "[woa-background-image]"
})
/**
 * Directive to load one of a number of alternative background images and optional corresponding classes and CSS variables.
 * Images are loaded using the ImageService.
 * Image detail CSS variables are referenced by the global cached-background-image class to apply the required styling.
 */
export class BackgroundImageDirective {
    private element: ElementRef = inject(ElementRef);
    private renderer: Renderer2 = inject(Renderer2);
    private imageService: CachedImageService = inject(CachedImageService);

    private filteredImageSpecs: ImageSpec[];
    private lastTimeApplied: Date;

    public readonly imageSpecs: InputSignal<ImageSpec[] | undefined> = input<ImageSpec[] | undefined>(undefined, {alias: "woa-background-image"});

    constructor() {
        effect(() => {
            const imageSpecs: ImageSpec[] | undefined = this.imageSpecs();
            if (imageSpecs) {
                this.filteredImageSpecs = imageSpecs.filter((imageSpec: ImageSpec) => this.shouldApplyImageSpec(imageSpec));
                void this.applyImageSpec();
            }
        });
    }

    public shouldApplyImageSpec(imageSpec: ImageSpec): boolean {
        return !(imageSpec.conditional && !imageSpec.on);
    }

    private async applyImageSpec(imageIdx: number = 0): Promise<void> {
        this.lastTimeApplied = new Date();

        // Out of options - remove any existing image and classes
        if (imageIdx >= this.filteredImageSpecs.length) {
            this.removeImage();
            this.removeImageStyles();
            return;
        }

        const imageSpec: ImageSpec = this.filteredImageSpecs[imageIdx];
        const isDefault: boolean = imageIdx === this.filteredImageSpecs.length - 1;

        // If the image object is unset, either image data is not yet loaded (entity is still loading),
        // or if this is the final alternative, we assume it is a final fallback class.
        if (!imageSpec.image) {
            this.removeImage();
            this.removeImageStyles();
            if (isDefault) {
                this.applyImageStyles(imageSpec);
            }
        }

            // We use the image data directly if present - this case caters for previews where the image id may be unset or
        // will reference the currently saved image.
        else if (imageSpec.image.image) {
            this.applyImage(imageSpec.image.image, imageSpec);
        }

        // If we have an image id, load the image from cache, and use the next alternative if we can't find it
        else if (imageSpec.image.id != undefined) {
            const thisTimeApplied: Date = this.lastTimeApplied;
            try {
                const base64Image: string = await this.imageService.getImage(imageSpec.image.id, imageSpec.image.hash);
                if (thisTimeApplied.getMilliseconds() === this.lastTimeApplied.getMilliseconds()) {
                    this.applyImage(base64Image, imageSpec);
                }
            } catch (error) {
                void this.applyImageSpec(imageIdx + 1);
            }
        }

        // If no id and no image data, then there is no image configured for this alternative - use the next alternative
        else {
            void this.applyImageSpec(imageIdx + 1);
        }
    }

    private removeImage(): void {
        CssVarHelper.applyValue(this.element.nativeElement, "--cached-background-image-content", undefined);
        CssVarHelper.applyValue(this.element.nativeElement, "--cached-background-image-url", undefined);
        CssVarHelper.applyValue(this.element.nativeElement, "--cached-background-image-opacity", undefined);
        this.renderer.removeClass(this.element.nativeElement, "cached-background-image");
        this.renderer.removeClass(this.element.nativeElement, "cached-background-image-tiled");
    }

    private applyImage(base64Image: string, imageSpec: ImageSpec) {
        this.removeImageStyles();
        this.applyImageStyles(imageSpec);
        CssVarHelper.applyValue(this.element.nativeElement, "--cached-background-image-content", "''");
        CssVarHelper.applyValue(this.element.nativeElement, "--cached-background-image-url", "url(data:image/png;base64," + base64Image + ")");
        CssVarHelper.applyValue(this.element.nativeElement, "--cached-background-image-opacity", imageSpec.opacity);
        this.renderer.addClass(this.element.nativeElement, imageSpec.tiled ? "cached-background-image-tiled" : "cached-background-image");
    }

    private removeImageStyles(): void {
        const imageSpecs: ImageSpec[] | undefined = this.imageSpecs();
        if (imageSpecs) {
            imageSpecs.forEach((imageSpec: ImageSpec) => {
                if (imageSpec.classes) {
                    imageSpec.classes.split(" ").forEach((cssClass: string) => {
                        if (cssClass) {
                            this.renderer.removeClass(this.element.nativeElement, cssClass);
                        }
                    });
                }
                if (imageSpec.cssVars) {
                    CssVarHelper.removeVars(this.element.nativeElement, imageSpec.cssVars);
                }
            });
        }
    }

    private applyImageStyles(imageSpec?: ImageSpec): void {
        if (imageSpec) {
            if (imageSpec.classes) {
                imageSpec.classes.split(" ").forEach((cssClass: string) => {
                    if (cssClass) {
                        this.renderer.addClass(this.element.nativeElement, cssClass);
                    }
                });
            }
            if (imageSpec.cssVars) {
                CssVarHelper.applyVars(this.element.nativeElement, imageSpec.cssVars);
            }
        }
    }
}
